import { motion } from "framer-motion"
import styled from "@emotion/styled"
import { css } from "@emotion/react"

import { a11yOutline } from "css/a11y"
import getMediaQuery from "css/breakpoints"
import { buttonReset } from "css/buttons"
import { fullGrid } from "css/grid"
import { body40, bold, legend100, visuallyHidden } from "css/text"

import { cssVars } from "components/nav-v2/Wrapper"

export const navPaneInputWidth = css`
  width: calc(var(--grid-col-unit) * 6 + var(--grid-gap) * 5);
  margin-inline: auto;
  max-width: 500px;
`

export const Form = styled.form`
  ${navPaneInputWidth}
  margin-block-start: 2.5rem;

  ${getMediaQuery("m")} {
    margin-block-start: 8.25rem;
  }
`

export const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  justify-content: center;

  input[type="search"]::-webkit-search-cancel-button {
    -webkit-appearance: none;
  }
`

export const Input = styled.input`
  font-family: var(--primary-font-family);
  padding-inline-start: 2.9375rem;
  padding-inline-end: 2.5rem;
  border-radius: 50px;
  border: none;
  appearance: none;
  -webkit-appearance: none;
  padding-block: 0.625rem;
  background-color: rgb(var(--light-grey));
  color: rgb(var(--light-black));
  font-size: 1rem;

  &:focus,
  &:focus-visible {
    outline: none;
  }
  &.keyboard-focus {
    ${a11yOutline}
  }

  ::placeholder {
    color: rgb(var(--dark-grey));
  }
`

export const SearchButton = styled.button`
  ${buttonReset}
  position: absolute;
  inset-inline-start: 0;
  padding-inline: 1.25rem;

  > svg {
    fill: rgb(var(--dark-grey));
    margin-block-start: 0.25rem;
  }
`

export const ClearButton = styled.button`
  ${buttonReset}
  position: absolute;
  inset-inline-end: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  background: rgb(var(--green));

  > svg {
    fill: rgb(var(--pure-white));
    width: 0.4rem;
    height: 0.4rem;
  }
`

export const ListRoot = styled(motion.div)`
  position: relative;
  box-sizing: border-box;
  margin: 0;
  width: 100%;
  padding-block-start: 1.25rem;
`

export const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

export const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-block-end: 1rem;
`

export const TitleLine = styled(motion.span)`
  ${body40}
  color: rgb(var(--green));
  font-weight: 700;
`

export const ClearHistoryButton = styled.button`
  ${buttonReset}
  cursor: pointer;
  color: rgb(var(--black));
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 12px;
  font-weight: 400;

  span {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    background: #f2f2f2;
  }

  svg {
    fill: rgb(var(--black));
    width: 0.375rem;
    height: 0.375rem;
  }
`

export const InnerList = styled(motion.ul)`
  padding: 0;
  margin: 0;
  list-style: none;
  box-sizing: border-box;
  width: 100%;
`

export const FlexLi = styled(motion.li)`
  display: flex;
  align-items: center;
  padding: 0;
  margin: 0;
  width: 100%;
`

export const ResultLine = styled.button`
  ${buttonReset}
  cursor: pointer;
  text-align: unset;
  color: rgb(var(--dark-grey));
  padding-block: 0.3125rem;
  margin: 0;
  width: 100%;
  font-size: 1rem;
  font-weight: 400;
  ${body40}

  ${getMediaQuery("m")} {
    @media (hover: hover) {
      &:hover {
        font-weight: 700;
      }
    }
  }

  strong {
    font-weight: 700;
    color: rgb(var(--light-black));
  }
`

export const SuggestionResultLine = styled(ResultLine)`
  font-weight: 400;

  * {
    font-weight: inherit;
  }

  strong {
    font-weight: 700;
  }
`

export const ScreenReaderDiv = styled.div`
  position: absolute;
  left: -9999px;

  [dir="rtl"] & {
    left: auto;
    right: -9999px;
  }
`

// --- Page-level styles (SearchResults page) ---

export const PageRoot = styled.div``

export const PageA11yHeading = styled.h1`
  ${visuallyHidden}
`

export const PageHeader = styled.div`
  display: flex;
  position: sticky;
  flex-direction: column;
  width: 100vw;
  align-items: center;
  justify-content: center;
  height: fit-content;
  z-index: 2;
  top: calc(var(${cssVars.main.safe}) * var(${cssVars.main.bar.pinned}));
  transform: translateY(-2px);
  outline: 1px solid transparent;
  background: rgb(var(--pure-white));

  &:before {
    content: "";
    position: absolute;
    top: -100px;
    left: 0;
    right: 0;
    background: rgb(var(--pure-white));
    height: calc(100px + 100% + var(${cssVars.main.bar.pinned}) * 20px);
  }
`

export const PageFormRoot = styled.div`
  ${fullGrid}
`

export const PageFormContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  box-sizing: border-box;
  grid-column: col 1 / span 6;

  ${getMediaQuery("m")} {
    grid-column: col 4 / span 6;
  }
`

export const PageFormWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  box-sizing: border-box;

  ${navPaneInputWidth}
  grid-column: doc;
`

export const PageTabsContainer = styled.div`
  display: block;
  width: 100%;

  ${getMediaQuery("m")} {
    ${fullGrid}
    grid-column: col 1 / span 6;
  }
`

// --- Page search input overrides ---

export const PageInputForm = styled.form`
  width: 100%;
  max-width: 500px;
  margin-inline: auto;
  position: relative;
  padding-block: 1.2rem;
`

// --- Tab styles ---

export const TabsRoot = styled.div`
  width: 100%;

  ${getMediaQuery("m")} {
    ${fullGrid}
  }
`

export const TabsWrapper = styled.div`
  position: relative;
  width: calc(100% - var(--grid-gap) * 2);
  max-width: var(--vw100, 100vw);
  margin-inline: auto;
  grid-column: main-start / main-end;
  overflow-x: auto;
  overflow-y: hidden;
  padding-block-start: 1.5rem;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`

export const TabsList = styled.ul`
  text-align: center;
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  white-space: nowrap;
  width: max-content;
  min-width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
`

export const TabItem = styled.li`
  position: relative;
  display: flex;
  align-items: center;
`

export const TabLabel = styled.label`
  ${bold}
  cursor: pointer;
  user-select: none;
  overflow: hidden;
  color: rgb(var(--light-black));
  border: 1.5px solid transparent;
  border-radius: 50px;
  outline: none;
  background-color: transparent;
  transition: all 0.3s;
  margin-inline: 0.375rem;
  padding: 0.375rem 1rem;

  :hover {
    @media (hover: hover) and (pointer: fine) {
      color: rgb(var(--green));
    }
  }
`

export const TabInput = styled.input`
  position: absolute;
  top: -10px;
  inset-inline-start: -10px;
  opacity: 0;

  &:checked + label {
    color: rgb(var(--green));
  }

  &.focus-visible + label {
    outline: 2px solid rgb(var(--focus, 0 255 255) / 1);
    outline-offset: -2px;
  }
`
